import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReturnOrderHeader } from 'returnCenter-api';
import {
    clearSearchOrders,
    orderSelectors,
    orderThunks,
    selectOrderSearchFilters,
    setOrderSearchFilters,
} from 'store/reducers/orderSlice';
import { returnSelectors } from 'store/reducers/returnSlice';
import { selectIsOrderTypeExcluded } from 'store/reducers/orderSlice';

import SearchModal, { SearchMode } from 'components/Search/SearchModal';
import useAppSelector from 'hooks/useAppSelector';
import useDebounce from 'hooks/useDebounce';
import { configSelectors } from 'store/reducers/configSlice';
import { faExclamationTriangle, faFileCircleXmark } from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

type AdditionalOrderEditorProps = {
    returnStatus: number | null;
    isModalOpen?: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    statusButtonLabel: string;
    isReturnSlip?: boolean;
    initialSearchMode?: SearchMode;
};

const AdditionalOrderEditor = ({ isModalOpen, setIsModalOpen, initialSearchMode }: AdditionalOrderEditorProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const additionalOrder = useAppSelector(orderSelectors.selectOrderHeaders);
    const additionalOrderError = useAppSelector(orderSelectors.selectErrorMessage);
    const additionalOrderLoading = useAppSelector(orderSelectors.selectSearchLoading);
    const barcodeLookupLoading = useAppSelector(orderSelectors.selectBarcodeLookupLoading);
    const orderDeliveryInfo = useAppSelector(orderSelectors.selectOrderDeliveryInfo);
    const activeOrdersInSession = useAppSelector(returnSelectors.selectOrderNumbers);
    const selectedOrderNumbers = useAppSelector(returnSelectors.selectSelectedOrderNumbers);
    const customerCode = useAppSelector(returnSelectors.selectCustomerDetails)?.customerCode;
    const { from, to } = useAppSelector(selectOrderSearchFilters);
    const isOrderTypeExcluded = useAppSelector(selectIsOrderTypeExcluded);
    const settingsAvailable = useAppSelector(configSelectors.selectSettingsAvailable);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchMode, setSearchMode] = useState<SearchMode>('orderNumber');
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [statusIcon, setStatusIcon] = useState<IconDefinition | null>(null);
    const [modalSessionBaselineOrderNumbers, setModalSessionBaselineOrderNumbers] = useState<Set<string> | null>(null);

    const isBarcodeSearchMode = searchMode === 'barcode';
    const isArticleSearchMode = searchMode === 'articleNumber';
    const isOrderNumberSearchMode = searchMode === 'orderNumber';

    const modalSessionAddedOrderNumbers = modalSessionBaselineOrderNumbers
        ? activeOrdersInSession.filter((orderNumber) => !modalSessionBaselineOrderNumbers.has(orderNumber))
        : [];

    const barcodeAddedOrders = modalSessionAddedOrderNumbers.map((orderNumber) => {
        const matchingDeliveryInfo = orderDeliveryInfo.find((info) => info.orderNumber === orderNumber);

        return {
            orderNumber,
            customerCode: customerCode?.toString() ?? '-',
            orderDate: matchingDeliveryInfo?.orderDate ?? '-',
        };
    });

    const trimmed = searchQuery.trim();

    const setBarcodeStatusFeedback = useCallback((message: string, icon: IconDefinition) => {
        setStatusMessage(message);
        setStatusIcon(icon);
    }, []);

    const handleSearch = useCallback(() => {
        if (!trimmed) return;

        // Always clear local status first - this takes priority
        setStatusMessage(null);
        setStatusIcon(null);

        if (searchMode === 'barcode') {
            const selectedCustomerCode = customerCode?.toString();
            if (!selectedCustomerCode) {
                setBarcodeStatusFeedback(
                    t('pleaseSelectCustomer', 'Please select a customer before scanning.'),
                    faExclamationTriangle,
                );
                return;
            }

            return dispatch(
                orderThunks.getOrderByBarcodeForCustomer({
                    barcode: trimmed,
                    customerCode: selectedCustomerCode,
                }),
            )
                .unwrap()
                .then((result) => {
                    if (result.type === 'return') {
                        const message = t('barcodeBelongsToReturn');
                        setBarcodeStatusFeedback(message, faExclamationTriangle);
                        return;
                    }

                    // A package in a batch gets a return slip of its own, from the start page.
                    if (result.type === 'batchPackage') {
                        const message = t(
                            'batchPackage.belongsToBatch',
                            'This package belongs to batch #{{batchId}}. Scan it from the start page to create its return slip.',
                            { batchId: result.batchPackages?.[0]?.batchReturnId },
                        );
                        setBarcodeStatusFeedback(message, faExclamationTriangle);
                        return;
                    }

                    if (result.type !== 'order' || !result.orderNumber) {
                        const message = t('orderNotFoundForBarcode');
                        setBarcodeStatusFeedback(message, faFileCircleXmark);
                        return;
                    }

                    if (
                        selectedOrderNumbers.includes(result.orderNumber) ||
                        activeOrdersInSession.includes(result.orderNumber)
                    ) {
                        const message = t('orderAlreadyAdded');
                        setBarcodeStatusFeedback(message, faExclamationTriangle);
                        return;
                    }

                    dispatch(
                        orderThunks.getOrderAndAddToReturnSlip({
                            orderId: result.orderNumber,
                            customerCode: selectedCustomerCode,
                            isAddingToExisting: true,
                            searchMode: searchMode,
                        }),
                    )
                        .unwrap()
                        .then(() => {
                            setSearchQuery('');
                        });
                })

                .catch((err) => {
                    if (err.message.includes('status code 422')) {
                        const message = t('scannedTrackingCodeNotBelongToCustomer');
                        setBarcodeStatusFeedback(message, faExclamationTriangle);
                        return;
                    }

                    const message = t('orderNotFoundForBarcode');
                    setBarcodeStatusFeedback(message, faFileCircleXmark);
                });
        }

        dispatch(setOrderSearchFilters({ query: trimmed }));

        const isArticleSearch = searchMode === 'articleNumber';
        return dispatch(
            orderThunks.searchOrders({
                searchString: trimmed,
                customerCode: customerCode?.toString(),
                searchArticles: isArticleSearch,
                from: from ? from?.toISOString() : undefined,
                to: to ? to?.toISOString() : undefined,
            }),
        );
    }, [
        dispatch,
        trimmed,
        customerCode,
        searchMode,
        from,
        to,
        t,
        selectedOrderNumbers,
        activeOrdersInSession,
        setBarcodeStatusFeedback,
    ]);

    const minLength = isArticleSearchMode ? 3 : isBarcodeSearchMode ? 8 : 2;
    const debouncedSearch = useDebounce(handleSearch, 350);

    // Trigger search when query length is valid
    useEffect(() => {
        if (trimmed.length >= minLength) {
            debouncedSearch();
        }
    }, [trimmed, from, to, minLength, debouncedSearch]);

    // Clear feedback when query is too short, reset on modal/mode changes
    useEffect(() => {
        // Clear feedback when query becomes too short (while typing/backspacing)
        if (isBarcodeSearchMode && trimmed.length < minLength) {
            if (statusMessage || statusIcon) {
                setStatusMessage(null);
                setStatusIcon(null);
            }
        }
    }, [trimmed, isBarcodeSearchMode, minLength, statusMessage, statusIcon]);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isModalOpen) {
            setModalSessionBaselineOrderNumbers(
                (prev) => prev ?? new Set([...selectedOrderNumbers, ...activeOrdersInSession]),
            );
        } else {
            setModalSessionBaselineOrderNumbers(null);
        }
    }, [isModalOpen, selectedOrderNumbers, activeOrdersInSession]);

    // Reset state when modal initially opens
    useEffect(() => {
        if (!isModalOpen) return;

        dispatch(clearSearchOrders());
        setSearchQuery('');
        setSearchMode(initialSearchMode ?? 'orderNumber');
        setStatusMessage(null);
        setStatusIcon(null);

        return () => {
            dispatch(setOrderSearchFilters({ query: '', from: undefined, to: undefined }));
        };
    }, [isModalOpen, dispatch, initialSearchMode]);

    // Reset when search mode changes
    useEffect(() => {
        dispatch(clearSearchOrders());
        setSearchQuery('');
        setStatusMessage(null);
        setStatusIcon(null);
    }, [isBarcodeSearchMode, isArticleSearchMode, isOrderNumberSearchMode, dispatch]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStatusMessage(null);
        setStatusIcon(null);
        setSearchQuery(e.target.value);
    };

    const handleRowClick = useCallback(
        (order: ReturnOrderHeader) => {
            if (!order || !settingsAvailable.isAvailable || isOrderTypeExcluded(order)) return;

            if (selectedOrderNumbers.includes(order.orderNumber?.toString() ?? '')) {
                toast.warn(t('orderAlreadyAdded'), { autoClose: 3000, closeOnClick: true });
                return;
            }

            if (activeOrdersInSession.includes(order.orderNumber?.toString() ?? '')) {
                toast.warn(t('orderAlreadyAdded'), { autoClose: 3000, closeOnClick: true });
                return;
            }

            dispatch(
                orderThunks.getOrderAndAddToReturnSlip({
                    orderId: order.orderNumber?.toString()!,
                    isAddingToExisting: true,
                    searchMode: isBarcodeSearchMode ? 'barcode' : isArticleSearchMode ? 'articleNumber' : 'orderNumber',
                }),
            );

            // Only close modal for non-barcode modes (order/article search)
            if (!isBarcodeSearchMode) {
                setIsModalOpen(false);
            }
        },
        [
            dispatch,
            t,
            setIsModalOpen,
            selectedOrderNumbers,
            activeOrdersInSession,
            isBarcodeSearchMode,
            isArticleSearchMode,
            isOrderTypeExcluded,
            settingsAvailable.isAvailable,
        ],
    );

    const tableCols = [
        t('orderNumber'),
        t('returnRegistered'),
        t('customerCode'),
        t('orderType'),
        t('goodsMarking'),
        t('orderDateTime'),
    ];

    const rows = useCallback(() => {
        return (additionalOrder ?? []).map((order) => {
            const orderNumber = order.orderNumber?.toString() ?? '';
            const selectedCustomerCode = customerCode?.toString().trim().toLowerCase();
            const orderCustomerCode = order.customerCode?.toString().trim().toLowerCase();
            const hasCustomerMismatch = !!selectedCustomerCode && orderCustomerCode !== selectedCustomerCode;
            const isAlreadyAdded =
                selectedOrderNumbers.includes(orderNumber) || activeOrdersInSession.includes(orderNumber);

            const isDisabled =
                !settingsAvailable.isAvailable ||
                isOrderTypeExcluded(order) ||
                (!!orderNumber && (isAlreadyAdded || hasCustomerMismatch));
            const selectable = !!orderNumber && !isDisabled;

            return {
                cells: [
                    { el: <Text>{order.orderNumber}</Text> },
                    { el: <Text>{order.hasReturnSlip ? t('Yes') : t('No')}</Text> },
                    { el: <Text>{order.customerCode}</Text> },
                    { el: <Text>{order.orderType}</Text> },
                    { el: <Text>{order.packageLabel}</Text> },
                    { el: <Text>{order.orderDate?.str}</Text> },
                ],
                onClick: selectable ? () => handleRowClick(order) : undefined,
                faded: !selectable,
            };
        });
    }, [
        additionalOrder,
        handleRowClick,
        t,
        customerCode,
        selectedOrderNumbers,
        activeOrdersInSession,
        isOrderTypeExcluded,
        settingsAvailable.isAvailable,
    ]);

    return (
        <>
            {isModalOpen && (
                <SearchModal
                    rows={rows}
                    tableCols={tableCols}
                    clearSearchOrders={clearSearchOrders}
                    handleInputChange={handleInputChange}
                    setIsModalOpen={setIsModalOpen}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    handleSearch={handleSearch}
                    searchResults={additionalOrder ?? []}
                    loadingOrders={additionalOrderLoading}
                    errorMessage={additionalOrderError}
                    showSearchModeToggle
                    onSearchModeChange={setSearchMode}
                    statusMessage={statusMessage}
                    statusIcon={statusIcon}
                    barcodeLookupLoading={barcodeLookupLoading}
                    barcodeAddedOrders={barcodeAddedOrders}
                    isBarcodeSearchMode={isBarcodeSearchMode}
                    isArticleSearchMode={isArticleSearchMode}
                    isOrderNumberSearchMode={isOrderNumberSearchMode}
                />
            )}
        </>
    );
};

export default AdditionalOrderEditor;
